import axios from 'axios';
import { debugLog, debugWarn, debugError } from '../../utils/debug';

// Get API URL from environment variable or use default
// IMPORTANT: Use getApiUrl() at runtime, not API_URL constant, to ensure correct URL on mobile
import { getApiUrl } from '../../utils/apiUtils';

// Import DEBUG_ENABLED for manual group management
const isDevelopment = process.env.NODE_ENV === 'development';
const DEBUG_ENABLED = process.env.REACT_APP_DEBUG === 'true' || isDevelopment;

// Get the selected prompt set ID from localStorage
export const getSelectedPromptSetId = () => {
  try {
    const saved = localStorage.getItem('selectedPromptSetId');
    return saved && saved !== 'null' ? parseInt(saved) : null;
  } catch (error) {
    debugWarn('Error getting selected prompt set ID:', error);
    return null;
  }
};

// Switch coach for a conversation
export const switchConversationCoach = async (user, conversationId, promptSetId = null) => {
  const token = await getToken(user);
  
  const API_URL = getApiUrl(); // Get at runtime
  const response = await axios.put(
    `${API_URL}/messages/conversations/${conversationId}/coach`,
    { prompt_set_id: promptSetId },
    {
      headers: { Authorization: `Bearer ${token}` }
    }
  );
  
  return response.data;
};

// Get the user's token for API requests
export const getToken = async (user) => {
  try {
    // Get a proper JWT token if possible
    if (user && user.getToken) {
      try {
        // Try to get a JWT token from Clerk
        const token = await user.getToken();
        if (token && token.length > 0) {
          return token;
        }
      } catch (tokenError) {
        debugError('Failed to get JWT token from Clerk:', tokenError);
      }
    }
    
    // Use the user ID as a fallback for development/testing
    if (user && user.id) {
      // Ensure the user ID is in the correct format (starts with "user_")
      if (user.id.startsWith('user_')) {
        return user.id;
      } else {
        // If it's just a numeric ID, try to find the clerk_id from the user object
        if (user.externalAccounts && user.externalAccounts.length > 0) {
          const clerkId = user.externalAccounts[0].id;
          if (clerkId && clerkId.startsWith('user_')) {
            return clerkId;
          }
        }
        // Last resort: return the user ID as-is
        return user.id;
      }
    }
    debugError('No user or user.id available');
    throw new Error('No authentication token available');
  } catch (error) {
    debugError('Error getting token:', error);
    throw new Error('Authentication error: ' + error.message);
  }
};

// Fetch all conversations for a user
export const fetchConversations = async (user, timeoutMs = 30000) => {
  const token = await getToken(user);
  
  try {
    const response = await axios.get(`${getApiUrl()}/messages/conversations`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: timeoutMs, // Configurable timeout (default 30s, shorter for new users)
    });
    return response.data;
  } catch (error) {
    // Provide more detailed error information
    if (error.code === 'ECONNABORTED') {
      debugError(`Error fetching conversations: Request timed out after ${timeoutMs}ms`);
    } else if (error.response) {
      debugError(`Error fetching conversations: ${error.response.status} ${error.response.statusText}`);
    } else if (error.request) {
      debugError('Error fetching conversations: No response received from server');
    } else {
      debugError('Error fetching conversations:', error.message);
    }
    throw error;
  }
};

// Get the last message for a conversation
export const fetchLastMessage = async (user, conversationId) => {
  const token = await getToken(user);
  const response = await axios.get(
    `${getApiUrl()}/messages/conversations/${conversationId}?limit=1`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  
  // If there's at least one message, use it as the lastMessage
  const lastMessage = response.data.messages?.length > 0
    ? { 
        ...response.data.messages[0],
        is_user: response.data.messages[0].role === 'user'
      }
    : null;
  
  return lastMessage;
};

// Create a new conversation
export const createNewConversation = async (user, promptSetId = null, signal = null) => {
  const token = await getToken(user);
  
  // Get user's timezone offset
  const timezoneOffset = new Date().getTimezoneOffset() * -1; // Convert to minutes from UTC (positive for ahead of UTC)
  
  const requestBody = {
    timezone_offset: timezoneOffset
  };
  
  // Only add prompt_set_id if one is provided
  if (promptSetId) {
    requestBody.prompt_set_id = promptSetId;
  }
  
  const response = await axios.post(
    `${getApiUrl()}/messages/conversations`,
    requestBody,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      timeout: 25000, // 25 second timeout (longer than UI timeout to allow proper error handling)
      signal: signal, // Allow cancellation via AbortController
    }
  );
  return response.data;
};

// Fetch messages for a specific conversation
export const fetchConversationMessages = async (user, conversationId, since = null) => {
  const token = await getToken(user);
  
  let url = `${getApiUrl()}/messages/conversations/${conversationId}`;
  if (since) {
    url += `?since=${encodeURIComponent(since)}`;
  }
  
  try {
    // 🎯 FIX: Add timeout to prevent hanging
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000, // 10 second timeout
    });
    
    return response.data;
  } catch (error) {
    debugError('Error fetching conversation messages:', error.message);
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      debugError('❌ [API] Request timed out after 10 seconds');
    }
    throw error;
  }
};


// Delete a conversation
export const deleteConversation = async (user, conversationId) => {
  const token = await getToken(user);
  
  await axios.delete(`${getApiUrl()}/messages/conversations/${conversationId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

// Get conversation context information
export const fetchConversationContext = async (user, conversationId) => {
  const token = await getToken(user);
  const promptSetId = getSelectedPromptSetId();
  
  // Get user's timezone offset
  const timezoneOffset = new Date().getTimezoneOffset() * -1; // Convert to minutes from UTC (positive for ahead of UTC)
  
  const params = {
    timezone_offset: timezoneOffset
  };
  
  // Only add prompt_set_id if one is selected
  if (promptSetId) {
    params.prompt_set_id = promptSetId;
  }
  
  const response = await axios.get(`${getApiUrl()}/messages/conversations/${conversationId}/context`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: params
  });
  
  return response.data;
};

// Get long-term memory for the current user
export const fetchLongTermMemory = async (user) => {
  const token = await getToken(user);
  
  // Get user's timezone offset
  const timezoneOffset = new Date().getTimezoneOffset() * -1; // Convert to minutes from UTC (positive for ahead of UTC)
  
  const response = await axios.get(`${getApiUrl()}/messages/memory`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      timezone_offset: timezoneOffset
    }
  });
  
  return response.data;
};

// Trigger an update of long-term memory for a conversation (developer use)
export const updateLongTermMemory = async (user, conversationId) => {
  const token = await getToken(user);
  
  // Get user's timezone offset
  const timezoneOffset = new Date().getTimezoneOffset() * -1; // Convert to minutes from UTC (positive for ahead of UTC)
  
  const response = await axios.post(
    `${getApiUrl()}/messages/conversations/${conversationId}/update-memory`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        timezone_offset: timezoneOffset
      }
    }
  );
  
  return response.data;
};

// Stream a message with real-time responses
export const streamMessage = async (user, conversationId, content, onMessage, onTyping, onError, onDone, isResend = false, execute_plan = false, adjust_plan = false) => {
  // Create an AbortController for cancellation
  const controller = new AbortController();
  
  try {
    const token = await getToken(user);
    const promptSetId = getSelectedPromptSetId();
    
    const requestBody = {
      content,
      role: 'user',
      timezone_offset: new Date().getTimezoneOffset() * -1,
      is_resend: isResend,
      execute_plan: execute_plan,
      adjust_plan: adjust_plan
    };
    
    // Only add prompt_set_id if one is selected
    if (promptSetId) {
      requestBody.prompt_set_id = promptSetId;
    }
    
    // Create a timeout for the fetch request (10 seconds)
    const fetchTimeout = setTimeout(() => {
      debugError('Stream request timed out after 10 seconds');
      controller.abort();
    }, 10000);
    
    // Use fetch with streaming to connect to the SSE endpoint
    let response;
    try {
      response = await fetch(`${getApiUrl()}/messages/conversations/${conversationId}/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      clearTimeout(fetchTimeout);
    } catch (fetchError) {
      clearTimeout(fetchTimeout);
      debugError('Stream fetch failed:', fetchError);
      throw fetchError;
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      debugError('Stream HTTP error:', response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    
    // Return the controller immediately so caller can cancel if needed
    const streamPromise = (async () => {
      try {
        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonStr = line.slice(6);
                let data;
                try {
                  data = JSON.parse(jsonStr);
                } catch (parseError) {
                  // Handle JSON parsing errors (e.g., bad Unicode escapes, malformed JSON)
                  // Extract error position if available
                  const positionMatch = parseError.message.match(/position (\d+)/);
                  const errorPosition = positionMatch ? parseInt(positionMatch[1]) : null;
                  
                  // Get context around the error position
                  let contextPreview = '';
                  if (errorPosition !== null && jsonStr.length > 0) {
                    const start = Math.max(0, errorPosition - 50);
                    const end = Math.min(jsonStr.length, errorPosition + 50);
                    contextPreview = jsonStr.substring(start, end);
                    if (start > 0) contextPreview = '...' + contextPreview;
                    if (end < jsonStr.length) contextPreview = contextPreview + '...';
                  } else {
                    contextPreview = jsonStr.substring(0, 200) + (jsonStr.length > 200 ? '...' : '');
                  }
                  
                  // Log detailed error information in a collapsed group
                  if (DEBUG_ENABLED) {
                    console.groupCollapsed(`❌ [SSE] JSON Parse Error: ${parseError.message.split(' at ')[0]}`);
                  }
                  debugError('Error parsing SSE data:', {
                    error: parseError.message,
                    errorType: parseError.name,
                    position: errorPosition !== null ? `${errorPosition} of ${jsonStr.length}` : 'unknown',
                    contextPreview: contextPreview,
                    isUnicodeError: parseError.message.includes('Unicode'),
                    fullLength: jsonStr.length
                  });
                  if (DEBUG_ENABLED) {
                    console.groupEnd();
                  }
                  
                  // Skip this malformed line - don't attempt to fix it as that could introduce other issues
                  continue;
                }
                
                // Debug: log all SSE event types for troubleshooting
                debugLog(`[SSE] Received event type: ${data.type}`);

                switch (data.type) {
                  case 'llm_debug':
                    // 🚀 LLM Debug Logging to DevTools
                    if (data.event === 'started') {
                      debugLog(`🚀 [LLM] ${data.model} - ${data.operation} - Started`);
                    } else if (data.event === 'completed') {
                      debugLog(`⏱️ [LLM] ${data.model} - ${data.operation} - Completed (${data.duration.toFixed(1)}s, ${data.message_count} messages)`);
                    }
                    break;
                  case 'debug':
                    // 🐛 Debug Events Logging to DevTools
                    if (data.event === 'do_not_respond') {
                      debugLog(`🤐 [DEBUG] AI chose not to respond: ${data.message}`);
                    }
                    break;
                  case 'typing':
                    debugLog('[SSE] Typing event received, calling onTyping callback');
                    if (onTyping) onTyping();
                    break;
                  case 'user_message':
                    if (onMessage) {
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'user',
                        is_user: true,
                        created_at: data.created_at,
                        replace_temp: true // Flag to indicate this should replace a temp message
                      });
                    }
                    break;
                  case 'message':
                    if (onMessage) {
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'assistant',
                        is_user: false,
                        created_at: data.created_at
                      });
                    }
                    break;
                  case 'schedule_success':
                    if (onMessage) {
                      // Update existing loading message with success status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_scheduled: true,
                        is_status_update: true
                      });
                      
                      // 🎯 NEW: Trigger schedule refresh when we get schedule updates
                      // This ensures the schedule tab shows the latest data
                      setTimeout(() => {
                        debugLog('Triggering schedule refresh after schedule status update');
                        window.dispatchEvent(new Event('runium:refresh-schedule'));
                      }, 2000); // Increased delay to avoid connection queueing with chat requests
                    }
                    break;
                  case 'schedule_loading':
                    if (onMessage) {
                      // Show loading indicator for scheduled message
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_scheduled: true,
                        is_loading: true
                      });
                    }
                    break;
                  case 'schedule_error':
                    if (onMessage) {
                      // Update existing loading message with error status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_scheduled: true,
                        is_error: true,
                        is_status_update: true
                      });
                    }
                    break;
                  case 'reassess_loading':
                    if (onMessage) {
                      // Show loading indicator for reassess operation
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_reassess: true,
                        is_loading: true
                      });
                    }
                    break;
                  case 'reassess_success':
                    if (onMessage) {
                      // Update existing loading message with success status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_reassess: true,
                        is_status_update: true
                      });
                      
                      // Trigger app-wide refresh to restart onboarding
                      setTimeout(() => {
                        debugLog('Triggering app refresh after reassess completion');
                        window.location.reload();
                      }, 2000); // 2 second delay to let user see the success message
                    }
                    break;
                  case 'reassess_error':
                    if (onMessage) {
                      // Update existing loading message with error status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_reassess: true,
                        is_error: true,
                        is_status_update: true
                      });
                    }
                    break;
                  case 'frequency_loading':
                    if (onMessage) {
                      // Show loading indicator for frequency update
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_frequency_update: true,
                        is_loading: true
                      });
                    }
                    break;
                  case 'frequency_success':
                    if (onMessage) {
                      // Update existing loading message with success status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_frequency_update: true,
                        is_status_update: true
                      });
                    }
                    break;
                  case 'frequency_error':
                    if (onMessage) {
                      // Update existing loading message with error status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_frequency_update: true,
                        is_error: true,
                        is_status_update: true
                      });
                    }
                    break;
                  case 'run_loading':
                    if (onMessage) {
                      // Show loading indicator for run tool action
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_run_tool: true,
                        is_loading: true
                      });
                    }
                    break;
                  case 'run_success':
                    if (onMessage) {
                      // Update existing loading message with success status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_run_tool: true,
                        is_status_update: true,
                        // Pass through any additional data (schedule_details, etc.)
                        ...data
                      });
                      
                      // Debug logging in development
                      if (process.env.NODE_ENV === 'development') {
                        debugLog('Run tool success data:', data);
                      }
                    }
                    break;
                  case 'run_error':
                    if (onMessage) {
                      // Update existing loading message with error status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_run_tool: true,
                        is_error: true,
                        is_status_update: true
                      });
                    }
                    break;
                  case 'regimen_loading':
                    if (onMessage) {
                      // Show loading indicator for regimen tool action
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_regimen_tool: true,
                        is_loading: true
                      });
                    }
                    break;
                  case 'regimen_success':
                    if (onMessage) {
                      // Update existing loading message with success status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_regimen_tool: true,
                        is_status_update: true,
                        // Pass through additional schedule data
                        created_schedules: data.created_schedules,
                        total_sessions: data.total_sessions,
                        week_start: data.week_start,
                        schedule_id: data.schedule_id,
                        updated_days: data.updated_days,
                        deleted_days: data.deleted_days,
                        completion_status: data.completion_status,
                        deleted_schedule_ids: data.deleted_schedule_ids,
                        deleted_count: data.deleted_count,
                        source_schedule_id: data.source_schedule_id,
                        new_schedule_ids: data.new_schedule_ids,
                        sessions_copied: data.sessions_copied
                      });
                      
                      
                      // Debug logging in development
                      if (process.env.NODE_ENV === 'development') {
                        debugLog('Regimen tool success data:', data);
                      }
                      
                      // 🎯 FIX: Trigger schedule refresh immediately when regimen tools succeed
                      // Backend sends this event only after schedule operations are complete,
                      // so we can refresh immediately without delays
                      debugLog('🔄 [REGIMENS] Triggering schedule refresh immediately after regimen_success event');
                      window.dispatchEvent(new Event('runium:refresh-schedule'));
                    }
                    break;
                  case 'regimen_error':
                    debugError('❌ [STREAM] Received regimen error event:', data);
                    if (onMessage) {
                      // Update existing loading message with error status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_regimen_tool: true,
                        is_error: true,
                        is_status_update: true
                      });
                    }
                    break;
                  case 'regimen_info':
                    if (onMessage) {
                      // Display informational message (e.g., "no changes needed")
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_regimen_tool: true,
                        is_info: true,
                        is_status_update: true
                      });
                    }
                    break;
                  case 'plan_button':
                    // 🎯 Multi-week training plan button
                    if (onMessage) {
                      onMessage({
                        id: `plan-button-${Date.now()}`,
                        content: '📋 Multi-week training plan ready',
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_plan_button: true,
                        plan_data: {
                          weeks: data.weeks,
                          start_date: data.start_date,
                          end_date: data.end_date,
                          description: data.description
                        }
                      });
                    }
                    break;
                  case 'debug_prompt_info':
                    // 🔍 DEBUG: Log prompt router info with LLM intent classifier details (for developers)
                    const emoji = {
                      'general_chat': '💬',
                      'workout_creation': '🏋️',
                      'help_mode': '❓',
                      'enhancement': '✨',
                      'workout_insertion': '📝',
                      'greeting_chat': '👋',
                      'workout_deletion': '🗑️',
                      'knowledge_base': '📚'
                    }[data.prompt_type] || '🤖';
                    
                    // Build compact header with LLM intent info if available
                    let headerText = `${emoji} [ROUTING] ${data.prompt_type}`;
                    if (data.llm_intent_info) {
                      const llm = data.llm_intent_info;
                      const source = llm.cache_hit && llm.source === 'llm' ? 'LLM cached' :
                                    llm.source === 'llm' ? 'LLM' : 
                                    llm.source === 'pattern' ? 'pattern' :
                                    llm.source === 'pattern_fallback' ? 'pattern fallback' :
                                    llm.source === 'fallback' ? 'fallback' : 'unknown';
                      const confidence = llm.confidence.toFixed(2);
                      const time = llm.duration_ms !== null && llm.duration_ms !== undefined ? 
                                   (llm.duration_ms < 10 ? '0.001s' : (llm.duration_ms / 1000).toFixed(2) + 's') : '?';
                      const workflow = llm.source === 'llm' && (data.prompt_type === 'workout_creation' || data.prompt_type === 'workout_deletion' || data.prompt_type === 'workout_insertion') ? ', workflow' : '';
                      const warning = llm.confidence < 0.7 ? ' ⚠️' : '';
                      const error = llm.source === 'fallback' ? ' ❌' : '';
                      headerText = `${emoji} [ROUTING] ${data.prompt_type} (${source}, ${confidence}, ${time}${workflow})${warning}${error}`;
                    }
                    
                    console.groupCollapsed(headerText);
                    debugLog(`💬 User: "${data.user_message_preview}"`);
                    
                    if (data.llm_intent_info) {
                      const llm = data.llm_intent_info;
                      debugLog(`🔍 Intent: ${llm.intent}`);
                      debugLog(`📊 Confidence: ${llm.confidence.toFixed(2)}${llm.confidence < 0.7 ? ' (low, using fallback)' : ''}`);
                      debugLog(`⚡ Source: ${llm.source}${llm.cache_hit ? ' (cached)' : ''}`);
                      debugLog(`🌐 Language: ${llm.language}`);
                      if (llm.duration_ms !== null && llm.duration_ms !== undefined) {
                        debugLog(`⏱️ Intent Classification: ${(llm.duration_ms / 1000).toFixed(3)}s`);
                      }
                      debugLog(`💾 Cache: ${llm.cache_hit ? 'hit' : 'miss'}`);
                      
                      // Pattern router action
                      if (llm.source === 'pattern') {
                        debugLog(`🔄 Pattern Router: matched (high confidence)`);
                      } else if (llm.source === 'pattern_fallback') {
                        debugLog(`🔄 Pattern Router: used (LLM confidence too low)`);
                        debugLog(`⚠️ Reason: LLM confidence < 0.7 threshold`);
                      } else if (llm.source === 'fallback') {
                        debugLog(`🔄 Pattern Router: used (LLM timeout/error)`);
                        debugLog(`❌ Error: LLM API timeout or error`);
                      } else {
                        debugLog(`🔄 Pattern Router: skipped (${llm.source === 'llm' && data.prompt_type === 'workout_creation' ? 'workflow continuation' : 'ambiguous case'})`);
                      }
                      
                      // Active workflow context
                      if (llm.source === 'llm' && (data.prompt_type === 'workout_creation' || data.prompt_type === 'workout_deletion' || data.prompt_type === 'workout_insertion')) {
                        debugLog(`📋 Active Workflow: ${data.prompt_type} (context provided to LLM)`);
                      }
                    } else {
                      // No LLM intent info - pattern router only or LLM disabled
                      debugLog(`⚡ Source: pattern router (LLM router disabled or not used)`);
                    }
                    
                    debugLog(`📏 Prompt Size: ${data.prompt_size_kb} KB`);
                    console.groupEnd();
                    break;
                  case 'debug_tool_info':
                    // 🧩 DEBUG: Show actual tool names the LLM used
                    debugLog(`🧩 [TOOL] ${data.tool_name} x${data.tool_count || 1}`);
                    break;
                  case 'onboarding_success':
                    if (onMessage) {
                      // Update message with onboarding success status
                      onMessage({
                        id: data.id,
                        content: data.content,
                        role: 'system',
                        is_user: false,
                        created_at: new Date().toISOString(),
                        is_onboarding_success: true
                      });
                      
                      // Calculate schedule generation time if start time is available
                      // Check all localStorage keys for schedule_start_time_* to find the user's start time
                      let scheduleStartTime = null;
                      for (let i = 0; i < localStorage.length; i++) {
                        const key = localStorage.key(i);
                        if (key && key.startsWith('schedule_start_time_')) {
                          scheduleStartTime = localStorage.getItem(key);
                          break;
                        }
                      }
                      
                      if (scheduleStartTime) {
                        const startTime = parseInt(scheduleStartTime, 10);
                        const totalTime = Date.now() - startTime;
                        const totalSeconds = (totalTime / 1000).toFixed(1);
                        debugLog(`✅ [SCHEDULE GENERATION COMPLETE] Total time: ${totalTime}ms / ${totalSeconds}s`);
                      } else {
                        debugLog('✅ [SCHEDULE GENERATION COMPLETE] (timing not available)');
                      }
                      
                      // 🎯 FIX: Trigger schedule refresh immediately when onboarding completes
                      // Backend sends this event only after schedule creation is complete,
                      // so we can refresh immediately without delays
                      debugLog('🔄 [ONBOARDING] Triggering schedule refresh immediately after onboarding_success event');
                      window.dispatchEvent(new Event('runium:refresh-schedule'));
                    }
                    break;
                  case 'done':
                    if (onDone) onDone('completed');
                    return;
                  case 'error':
                    debugError('Stream error:', data.content);
                    if (onError) onError(data.content);
                    return;
                  default:
                    // Unknown message type - silently ignore
                }
              } catch (e) {
                // This catch handles errors that occur after successful JSON parsing
                // (e.g., errors in message handling logic)
                debugError('Error handling SSE data:', {
                  error: e.message,
                  type: e.name,
                  stack: e.stack
                });
              }
            }
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          if (onDone) onDone('aborted');
          return;
        }
        throw error;
      } finally {
        // ⚠️ CRITICAL: Always release the reader to free up browser connections
        try {
          if (reader && !reader.closed) {
            reader.releaseLock();
          }
        } catch (e) {
          debugWarn('Error releasing reader lock:', e);
        }
      }
    })();
    
    return {
      controller,
      promise: streamPromise
    };
    
  } catch (error) {
    if (error.name === 'AbortError') {
      return { controller, promise: Promise.resolve() };
    }
    debugError('Error in streaming:', error);
    if (onError) onError(error.message);
    return { controller, promise: Promise.reject(error) };
  }
};

// Get user messaging settings
export const fetchUserMessagingSettings = async (user) => {
  const token = await getToken(user);
  
  const response = await axios.get(`${getApiUrl()}/messages/user-messaging-settings`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  
  return response.data;
};

// Update user messaging settings
export const updateUserMessagingSettings = async (user, settings) => {
  const token = await getToken(user);
  
  const response = await axios.put(`${getApiUrl()}/messages/user-messaging-settings`, settings, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  return response.data;
};

// Send a message to a conversation
export const sendMessage = async (user, conversationId, messageContent, options = {}) => {
  const token = await getToken(user);
  const promptSetId = getSelectedPromptSetId();
  
  // Get user's timezone offset
  const timezoneOffset = new Date().getTimezoneOffset() * -1; // Convert to minutes from UTC
  
  const messageData = {
    content: messageContent,
    timezone_offset: timezoneOffset,
    is_resend: options.isResend || false
  };
  
  // Only add prompt_set_id if one is selected
  if (promptSetId) {
    messageData.prompt_set_id = promptSetId;
  }
  
  const response = await axios.post(
    `${getApiUrl()}/messages/conversations/${conversationId}/messages`,
    messageData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );
  
  return response.data;
};

// Connection pool monitoring utility (silent - removed debug logs)
export const logConnectionStatus = () => {
  // Function kept for API compatibility but no longer logs
};

// Export getApiUrl for runtime API URL detection
export { getApiUrl }; 